import axiosInstance from "../api.axiosInstance"
import type {
  SolarDesignVersionsDto,
  SolarDesignVersionsResultDto,
} from "../dto/solar-design-versions.dto"

// A duplicate or switch checks equipment with PowurOS and runs production
// inline, so it gets the provider-write timeout rather than reporting a
// failure while the gateway is still saving.
const PROVIDER_WRITE_TIMEOUT_MS = 120_000

const versionsPath = (proposalId: string): string =>
  `/solar-proposal/v2/${encodeURIComponent(proposalId)}/design/versions`

const versionPath = (proposalId: string, designId: string): string =>
  `${versionsPath(proposalId)}/${encodeURIComponent(designId)}`

/** Solar Proposal V2's Vision design versions: the stored read and the inline writes. */
export const solarDesignVersionsService = {
  async getVersions(proposalId: string): Promise<SolarDesignVersionsDto> {
    const response = await axiosInstance.get<SolarDesignVersionsDto>(
      versionsPath(proposalId),
    )
    return response.data
  },

  /** The copy becomes the active design. */
  async postDuplicate(
    proposalId: string,
    body: { fromDesignId?: string; expectedActiveDesignId: string | null },
  ): Promise<SolarDesignVersionsResultDto> {
    const response = await axiosInstance.post<SolarDesignVersionsResultDto>(
      versionsPath(proposalId),
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )
    return response.data
  },

  async postActivate(
    proposalId: string,
    designId: string,
    /** Null when no design is active. */
    expectedActiveDesignId: string | null,
  ): Promise<SolarDesignVersionsResultDto> {
    const response = await axiosInstance.post<SolarDesignVersionsResultDto>(
      `${versionPath(proposalId, designId)}/activate`,
      { expectedActiveDesignId },
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )
    return response.data
  },

  /** An empty name restores the generated default. */
  async patchName(
    proposalId: string,
    designId: string,
    name: string,
  ): Promise<SolarDesignVersionsResultDto> {
    const response = await axiosInstance.patch<SolarDesignVersionsResultDto>(
      versionPath(proposalId, designId),
      { name },
    )
    return response.data
  },

  async postArchive(
    proposalId: string,
    designId: string,
  ): Promise<SolarDesignVersionsResultDto> {
    const response = await axiosInstance.post<SolarDesignVersionsResultDto>(
      `${versionPath(proposalId, designId)}/archive`,
    )
    return response.data
  },
}
